/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useEffect, useState } from 'react';
import { RootState, useAppDispatch, useAppSelector } from '../../store.model';
import { getZipControlUnitsByDeviceIds } from '../selectors';
import { isEqual } from 'lodash';
import LocationAPI from '../../../service/locations/LocationAPI';
import { fetchDeviceControlUnitsThunk } from '../actions/thunk';
import { useParams } from 'react-router-dom';
import useGetDeviceFromStoreByParam from '../../../service/device/hooks/useGetDeviceFromStoreParam';
import { DevicesStoreInfoResponse } from '../../../service/store/StoreService.model';
import { fromBase64 } from '../../../shared/util/base64';

interface Props {
  deviceId?: string;
  commanderId?: string;
  groupId?: string;
  controlUnitId?: string;
}
/**
 * Get and fetch control unit from specified device
 * @param deviceId
 * @returns [controlUnits, loading, error]
 * 
 * @usage
´´´ts
const [controlUnits, loading, error] = useControlUnitsByIds("UUUID_DEVICE")
´´´
 */
export function useControlUnitsByIds(
  props: Props,
  onError?: (error: unknown) => unknown
) {
  // const devicesIds: string[] = useAppSelector((state) =>
  //   getAllDevicesIdsByMode(state, props)
  // );
  const {
    storeId = props.groupId ?? '',
    deviceId = fromBase64(props.deviceId ?? ''),
    marketId,
  } = useParams();

  const {
    data: device,
    isLoading: isLoadingDevice,
    error: gettingDeviceError,
  } = useGetDeviceFromStoreByParam({
    storeId,
    deviceId,
  });

  const devices: (DevicesStoreInfoResponse | undefined)[] = [device];
  const { isFetching, isLoading } = LocationAPI.useGetLocationsQuery(marketId);
  // const [, devicesLoading] = useDevices(devicesIds);
  const devicesLoading = isFetching || isLoading || isLoadingDevice;
  const devicesControlUnits = useAppSelector(
    (state: RootState) =>
      getZipControlUnitsByDeviceIds(state, {
        devices: devices.filter(
          (device) => device !== undefined
        ) as DevicesStoreInfoResponse[],
        workspaceId: marketId,
      }),
    isEqual
  );
  const needFetch = devicesControlUnits.some(
    (deviceControlUnits) => deviceControlUnits.loading === undefined
  );
  const [fetch, setFetch] = useState(needFetch);
  const refetch = useCallback(() => {
    setFetch(true);
  }, []);
  const loading = devicesControlUnits.some(
    (deviceControlUnits) => deviceControlUnits.loading
  );
  const error = devicesControlUnits.some(
    (deviceControlUnits) => deviceControlUnits.error
  );

  // initial fetch
  const dispatch = useAppDispatch();
  useEffect(() => {
    if (fetch) {
      Promise.all(
        devicesControlUnits.map((deviceControlUnits) =>
          dispatch<any>(
            fetchDeviceControlUnitsThunk(
              deviceControlUnits.deviceId,
              deviceControlUnits.commanderId
            )
          )
        )
      )
        .catch(onError)
        .finally(() => setFetch(false));
      setFetch(false);
    }
  }, [fetch]);
  return [
    devicesControlUnits,
    loading || fetch || devicesLoading,
    error || Boolean(gettingDeviceError),
    refetch,
  ] as const;
}
